import axios from "axios";
import { toast } from "sonner";

const ADMIN_KEY = "salty-moo-admin";
export const clearAdminSession = () => {
  sessionStorage.removeItem(ADMIN_KEY);
  localStorage.removeItem(ADMIN_KEY);
};

// Expired admin session mid-edit: keep the page (and any unsaved text) and ask to sign in again.
axios.interceptors.response.use(undefined, (error) => {
  const sentToken = error?.config?.headers?.["x-admin-passcode"];
  if (error?.response?.status === 401 && sentToken && !error.config.url?.endsWith("/admin/session")) {
    clearAdminSession();
    toast("Your admin session has ended.", {
      id: "admin-session-ended",
      description: "Copy any unsaved text, then refresh the page and sign in again.",
      duration: 15000,
    });
  }
  return Promise.reject(error);
});

export const verifyAdminSession = async (token) => {
  await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/session`, {
    headers: { "x-admin-passcode": token },
  });
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const getSaltBlock = async () => {
  const { data } = await axios.get(`${API}/salt-block`);
  return data;
};

export const updateSaltBlock = async (payload, passcode) => {
  const { data } = await axios.put(`${API}/salt-block`, payload, {
    headers: { "x-admin-passcode": passcode },
  });
  return data;
};

export const getSaltieShoutOut = async () => {
  const { data } = await axios.get(`${API}/saltie-shout-out`);
  return data;
};

export const updateSaltieShoutOut = async (payload, passcode) => {
  const { data } = await axios.put(`${API}/saltie-shout-out`, payload, {
    headers: { "x-admin-passcode": passcode },
  });
  return data;
};

export const uploadSaltieShoutOutPhoto = async (file, passcode) => {
  const form = new FormData();
  form.append("file", file);
  const { data } = await axios.post(`${API}/admin/saltie-shout-out/photo`, form, {
    headers: { "x-admin-passcode": passcode },
  });
  return data;
};

export const listSaltieShoutOutArchive = async () => {
  const { data } = await axios.get(`${API}/saltie-shout-outs/archive`);
  return data;
};

export const getArchivedSaltieShoutOut = async (id) => {
  const { data } = await axios.get(`${API}/saltie-shout-outs/archive/${id}`);
  return data;
};

export const archiveAndRestartSaltieShoutOut = async (passcode) => {
  const { data } = await axios.post(
    `${API}/admin/saltie-shout-out/archive-and-restart`,
    {},
    { headers: { "x-admin-passcode": passcode } },
  );
  return data;
};

export const deleteArchivedSaltieShoutOut = async (id, passcode) => {
  const { data } = await axios.delete(
    `${API}/admin/saltie-shout-outs/archive/${id}`,
    { headers: { "x-admin-passcode": passcode } },
  );
  return data;
};

export const getRandomGrain = async () => {
  const { data } = await axios.get(`${API}/grains/random`);
  return data;
};


export const getSaltieWinksPage = async () => {
  const { data } = await axios.get(`${API}/saltie-winks-page`);
  return data;
};

export const updateSaltieWinksPage = async (payload, passcode) => {
  const { data } = await axios.put(`${API}/saltie-winks-page`, payload, {
    headers: { "x-admin-passcode": passcode },
  });
  return data;
};

export const getMeetTheHerdPage = async () => {
  const { data } = await axios.get(`${API}/meet-the-herd-page`);
  return data;
};

export const updateMeetTheHerdPage = async (payload, passcode) => {
  const { data } = await axios.put(`${API}/meet-the-herd-page`, payload, {
    headers: { "x-admin-passcode": passcode },
  });
  return data;
};

export const getLilSaltiesPage = async () => {
  const { data } = await axios.get(`${API}/lil-salties-page`);
  return data;
};

export const updateLilSaltiesPage = async (payload, passcode) => {
  const { data } = await axios.put(`${API}/lil-salties-page`, payload, {
    headers: { "x-admin-passcode": passcode },
  });
  return data;
};

export const getSaltieBraysPage = async () => {
  const { data } = await axios.get(`${API}/saltie-brays-page`);
  return data;
};

export const updateSaltieBraysPage = async (payload, passcode) => {
  const { data } = await axios.put(`${API}/saltie-brays-page`, payload, {
    headers: { "x-admin-passcode": passcode },
  });
  return data;
};

export const getSaltieMoosPage = async () => {
  const { data } = await axios.get(`${API}/saltie-moos-page`);
  return data;
};

export const updateSaltieMoosPage = async (payload, passcode) => {
  const { data } = await axios.put(`${API}/saltie-moos-page`, payload, {
    headers: { "x-admin-passcode": passcode },
  });
  return data;
};

export const getFrontPorchPage = async () => {
  const { data } = await axios.get(`${API}/front-porch-page`);
  return data;
};

export const updateFrontPorchPage = async (payload, passcode) => {
  const { data } = await axios.put(`${API}/front-porch-page`, payload, {
    headers: { "x-admin-passcode": passcode },
  });
  return data;
};

export const uploadHearABrayPhoto = async (file, passcode) => {
  const form = new FormData();
  form.append("file", file);
  const { data } = await axios.post(`${API}/admin/saltie-brays-page/hear-a-bray-photo`, form, {
    headers: { "x-admin-passcode": passcode },
  });
  return data;
};

export const uploadHearAMooPhoto = async (file, passcode) => {
  const form = new FormData();
  form.append("file", file);
  const { data } = await axios.post(`${API}/admin/saltie-moos-page/hear-a-moo-photo`, form, {
    headers: { "x-admin-passcode": passcode },
  });
  return data;
};

export const getPeekAMoo = async () => {
  const { data } = await axios.get(`${API}/peek-a-moo`);
  return data;
};

export const updatePeekAMoo = async (payload, passcode) => {
  const { data } = await axios.put(`${API}/peek-a-moo`, payload, {
    headers: { "x-admin-passcode": passcode },
  });
  return data;
};

// ---- Chapter content (editable copy per chapter) ----
export const getChapterContent = async (slug) => {
  const { data } = await axios.get(`${API}/chapter-content/${slug}`);
  return data;
};

export const updateChapterContent = async (slug, payload, passcode) => {
  const { data } = await axios.put(`${API}/chapter-content/${slug}`, payload, {
    headers: { "x-admin-passcode": passcode },
  });
  return data;
};

export const uploadChapterHero = async (slug, file, passcode) => {
  const form = new FormData();
  form.append("file", file);
  const { data } = await axios.post(
    `${API}/admin/chapter-content/${slug}/hero`,
    form,
    {
      headers: {
        "x-admin-passcode": passcode,
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return data;
};

export const uploadChapterFeaturePhoto = async (slug, file, passcode) => {
  const form = new FormData();
  form.append("file", file);
  const { data } = await axios.post(
    `${API}/admin/chapter-content/${slug}/feature-photo`,
    form,
    {
      headers: {
        "x-admin-passcode": passcode,
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return data;
};

export const subscribe = async (email) => {
  const { data } = await axios.post(`${API}/subscribe`, { email });
  return data;
};

export const adminLogin = async (passcode) => {
  const { data } = await axios.post(`${API}/admin/login`, { passcode });
  return data;
};

export const getStoryBarnItems = async () => {
  const { data } = await axios.get(`${API}/story-barn/items`);
  return data;
};

export const createStoryBarnItem = async (payload, passcode) => {
  const { data } = await axios.post(`${API}/story-barn/items`, payload, {
    headers: { "x-admin-passcode": passcode },
  });
  return data;
};

export const updateStoryBarnItem = async (slug, payload, passcode) => {
  const { data } = await axios.put(`${API}/story-barn/items/${slug}`, payload, {
    headers: { "x-admin-passcode": passcode },
  });
  return data;
};

export const deleteStoryBarnItem = async (slug, passcode) => {
  const { data } = await axios.delete(`${API}/story-barn/items/${slug}`, {
    headers: { "x-admin-passcode": passcode },
  });
  return data;
};

export const getWelcomeEmail = async (passcode) => {
  const { data } = await axios.get(`${API}/welcome-email`, {
    headers: { "x-admin-passcode": passcode },
  });
  return data;
};

export const updateWelcomeEmail = async (payload, passcode) => {
  const { data } = await axios.put(`${API}/welcome-email`, payload, {
    headers: { "x-admin-passcode": passcode },
  });
  return data;
};

export const sendWelcomeEmailTest = async (email, passcode) => {
  const { data } = await axios.post(
    `${API}/welcome-email/test`,
    { email },
    { headers: { "x-admin-passcode": passcode } }
  );
  return data;
};

// ---- Saltie Winks ----
export const getSaltieWinks = async () => {
  const { data } = await axios.get(`${API}/saltie-winks`);
  return data;
};

export const adminListSaltieWinks = async (passcode) => {
  const { data } = await axios.get(`${API}/admin/saltie-winks`, {
    headers: { "x-admin-passcode": passcode },
  });
  return data;
};

export const createSaltieWink = async (payload, passcode) => {
  const { data } = await axios.post(`${API}/saltie-winks`, payload, {
    headers: { "x-admin-passcode": passcode },
  });
  return data;
};

export const updateSaltieWink = async (id, payload, passcode) => {
  const { data } = await axios.put(`${API}/saltie-winks/${id}`, payload, {
    headers: { "x-admin-passcode": passcode },
  });
  return data;
};

export const deleteSaltieWink = async (id, passcode) => {
  const { data } = await axios.delete(`${API}/saltie-winks/${id}`, {
    headers: { "x-admin-passcode": passcode },
  });
  return data;
};

// ---- Character Photos ----
export const adminListPhotos = async (passcode) => {
  const { data } = await axios.get(`${API}/admin/photos`, {
    headers: { "x-admin-passcode": passcode },
  });
  return data;
};

export const adminUploadPhoto = async (slug, file, passcode, { preCropped = false } = {}) => {
  const form = new FormData();
  form.append("file", file);
  form.append("pre_cropped", preCropped ? "true" : "false");
  const { data } = await axios.post(`${API}/admin/photos/${slug}`, form, {
    headers: {
      "x-admin-passcode": passcode,
      "Content-Type": "multipart/form-data",
    },
  });
  return data;
};

export const adminDeletePhoto = async (slug, passcode) => {
  const { data } = await axios.delete(`${API}/admin/photos/${slug}`, {
    headers: { "x-admin-passcode": passcode },
  });
  return data;
};

// ---- Subscribers (admin only) ----
export const adminListSubscribers = async (
  passcode,
  { q = "", status = "", source = "", sort = "newest" } = {},
) => {
  const params = {};
  if (q) params.q = q;
  if (status) params.status = status;
  if (source) params.source = source;
  if (sort) params.sort = sort;
  const { data } = await axios.get(`${API}/admin/subscribers`, {
    params,
    headers: { "x-admin-passcode": passcode },
  });
  return data;
};

export const adminUpdateSubscriber = async (id, patch, passcode) => {
  const { data } = await axios.patch(
    `${API}/admin/subscribers/${id}`,
    patch,
    { headers: { "x-admin-passcode": passcode } },
  );
  return data;
};

export const adminDeleteSubscriber = async (id, passcode) => {
  const { data } = await axios.delete(`${API}/admin/subscribers/${id}`, {
    headers: { "x-admin-passcode": passcode },
  });
  return data;
};

export const adminSubscribersCsvUrl = (passcode) => {
  // Direct URL used inside a click handler; passcode is sent as header via
  // the fetch below (not exposed in the URL).
  return `${API}/admin/subscribers/export`;
};

export const downloadSubscribersCsv = async (passcode) => {
  const res = await fetch(`${API}/admin/subscribers/export`, {
    headers: { "x-admin-passcode": passcode },
  });
  if (res.status === 401) {
    clearAdminSession();
    throw new Error("Your admin session has ended — refresh and sign in again.");
  }
  if (!res.ok) throw new Error("Export failed");
  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const stamp = new Date().toISOString().slice(0, 10);
  a.download = `salty-moo-subscribers-${stamp}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// ---- Character Bios ----
export const getCharacterBios = async () => {
  const { data } = await axios.get(`${API}/character-bios`);
  return data;
};

export const adminListCharacterBios = async (passcode) => {
  const { data } = await axios.get(`${API}/admin/character-bios`, {
    headers: { "x-admin-passcode": passcode },
  });
  return data;
};

export const updateCharacterBio = async (slug, payload, passcode) => {
  const { data } = await axios.put(`${API}/admin/character-bios/${slug}`, payload, {
    headers: { "x-admin-passcode": passcode },
  });
  return data;
};

// ---- Saltie Fun Facts ----
export const getSaltieFunFacts = async () => {
  const { data } = await axios.get(`${API}/saltie-fun-facts`);
  return data;
};

export const updateSaltieFunFacts = async (payload, passcode) => {
  const { data } = await axios.put(`${API}/admin/saltie-fun-facts`, payload, {
    headers: { "x-admin-passcode": passcode },
  });
  return data;
};

// ---- Coloring Pages ----
export const getColoringPages = async () => {
  const { data } = await axios.get(`${API}/coloring-pages`);
  return data;
};

export const uploadColoringPage = async ({ file, title, verse, order }, passcode) => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("title", title);
  fd.append("verse", verse || "");
  fd.append("order", String(order || 0));
  const { data } = await axios.post(`${API}/admin/coloring-pages`, fd, {
    headers: { "x-admin-passcode": passcode, "Content-Type": "multipart/form-data" },
  });
  return data;
};

export const updateColoringPage = async (slug, payload, passcode) => {
  const { data } = await axios.put(`${API}/admin/coloring-pages/${slug}`, payload, {
    headers: { "x-admin-passcode": passcode },
  });
  return data;
};

export const deleteColoringPage = async (slug, passcode) => {
  const { data } = await axios.delete(`${API}/admin/coloring-pages/${slug}`, {
    headers: { "x-admin-passcode": passcode },
  });
  return data;
};

// ---- Saltie Selfies ----
export const getSaltieSelfies = async () => {
  const { data } = await axios.get(`${API}/saltie-selfies`);
  return data;
};

export const getPublicPhotos = async () => {
  const { data } = await axios.get(`${API}/photos`);
  return data;
};

export const uploadSaltieSelfie = async (file, { caption = "", order = 100, preCropped = false } = {}, passcode) => {
  const form = new FormData();
  form.append("file", file);
  form.append("caption", caption);
  form.append("order", String(order));
  form.append("pre_cropped", preCropped ? "true" : "false");
  const { data } = await axios.post(`${API}/admin/saltie-selfies`, form, {
    headers: {
      "x-admin-passcode": passcode,
      "Content-Type": "multipart/form-data",
    },
  });
  return data;
};

export const updateSaltieSelfie = async (id, payload, passcode) => {
  const { data } = await axios.put(`${API}/admin/saltie-selfies/${id}`, payload, {
    headers: { "x-admin-passcode": passcode },
  });
  return data;
};

export const deleteSaltieSelfie = async (id, passcode) => {
  const { data } = await axios.delete(`${API}/admin/saltie-selfies/${id}`, {
    headers: { "x-admin-passcode": passcode },
  });
  return data;
};

// ---- Audiobook ----
export const getAudiobook = async () => {
  const { data } = await axios.get(`${API}/audiobook`);
  return data;
};

export const uploadAudiobook = async (file, { title = "", subtitle = "" }, passcode) => {
  const form = new FormData();
  form.append("file", file);
  form.append("title", title);
  form.append("subtitle", subtitle);
  const { data } = await axios.post(`${API}/admin/audiobook`, form, {
    headers: {
      "x-admin-passcode": passcode,
      "Content-Type": "multipart/form-data",
    },
  });
  return data;
};

export const updateAudiobookMeta = async (payload, passcode) => {
  const { data } = await axios.put(`${API}/admin/audiobook/meta`, payload, {
    headers: { "x-admin-passcode": passcode },
  });
  return data;
};

export const deleteAudiobook = async (passcode) => {
  const { data } = await axios.delete(`${API}/admin/audiobook`, {
    headers: { "x-admin-passcode": passcode },
  });
  return data;
};


// ---------- Our Cow-munity ----------

export const getOurCowmunity = async () => {
  const { data } = await axios.get(`${API}/our-cow-munity`);
  return data;
};

export const getOurCowmunityDirectory = async () => {
  const { data } = await axios.get(`${API}/our-cow-munity/directory`);
  return data;
};

export const updateOurCowmunity = async (patch, passcode) => {
  const { data } = await axios.put(`${API}/admin/our-cow-munity`, patch, {
    headers: { "x-admin-passcode": passcode },
  });
  return data;
};
